/**
 * Accessibility Enhancement Utilities for Course Finder
 * Adapted from ILSTU Website Template main.js
 * @see https://cdn.illinoisstate.edu/js/template/4.0/main.js
 */

/**
 * Adds ARIA labelledby attributes to sections and articles based on their headings
 * DISABLED: We now use intentional aria-labelledby patterns in Vue components
 * This function is preserved for potential future use but does not run automatically
 */
export const addSectionAriaLabels = () => {
  // Intentional approach preferred - this function is now disabled
  // All regions should be explicitly labeled in Vue components using:
  // :aria-labelledby="sectionIds.regionName" and :id="sectionIds.regionName"
  return;
};

/**
 * Utility function to generate consistent section IDs from titles
 * Use this in Vue components to create intentional aria-labelledby connections
 * @param {string} title - The section title text
 * @returns {string} - Slugified ID suitable for DOM elements
 */
export const generateSectionId = (title) => {
  return title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
};

/**
 * Adds ARIA labels to phone number links for screen reader accessibility
 * Formats phone numbers with proper spacing and punctuation
 */
export const addPhoneAriaLabels = () => {
  document.querySelectorAll('a[href^="tel:"]').forEach((link) => {
    // Skip if already has aria-label
    if (link.getAttribute('aria-label')) return;

    const numbers = link.getAttribute('href').replace(/[^0-9]/g, "");
    const arr = numbers.split("");

    // Remove leading 1 for US numbers
    if (arr.length === 11 && arr[0] === "1") {
      arr.shift();
    }

    // Format 10-digit numbers with dots for screen reader clarity
    if (arr.length === 10) {
      arr[2] += ".";
      arr[5] += ".";
      arr[9] += ".";
      link.setAttribute('aria-label', arr.join(" "));
    }
  });
};

/**
 * Adds descriptive ARIA labels to links based on file type or destination
 * Helps screen reader users understand link context before clicking
 */
export const addLinkAriaLabels = () => {
  const linkSelectors = [
    { selector: 'a[download]', suffix: ' (Download)' },
    { selector: 'a[href$=".doc"], a[href$=".docx"]', suffix: ' (Word)' },
    { selector: 'a[href$=".xls"], a[href$=".xlsx"]', suffix: ' (Excel)' },
    { selector: 'a[href$=".ppt"], a[href$=".pptx"]', suffix: ' (PowerPoint)' },
    { selector: 'a[href$=".pdf"]', suffix: ' (PDF)' },
    { selector: 'a[href$=".zip"]', suffix: ' (ZIP)' },
    { selector: 'a[href^="http"]', suffix: ' (External)' }
  ];

  linkSelectors.forEach(({ selector, suffix }) => {
    document.querySelectorAll(selector).forEach((link) => {
      // Skip if already has aria-label
      if (link.getAttribute('aria-label')) return;

      const linkText = link.textContent.trim();
      if (linkText) {
        link.setAttribute('aria-label', linkText + suffix);
      }
    });
  });
};

/**
 * Initializes all accessibility enhancements
 * Run this function after DOM content is loaded or after dynamic content updates
 */
export const initAccessibilityEnhancements = () => {
  addSectionAriaLabels();
  addPhoneAriaLabels();
  addLinkAriaLabels();
};

/**
 * Debounced version of accessibility enhancements for frequent updates
 * Useful for Vue reactive content that updates frequently
 */
let accessibilityTimeout;
export const initAccessibilityEnhancementsDebounced = (delay = 250) => {
  clearTimeout(accessibilityTimeout);
  accessibilityTimeout = setTimeout(() => {
    initAccessibilityEnhancements();
  }, delay);
};